// Panel de detalle de una promo de referido (variante A del mock): premio de quien recomienda y, si
// se prende, del recomendado; a quién le aplica, el evento, cómo paga, condiciones y vigencia.
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
    type FieldErrors,
    type FieldPath,
} from 'react-hook-form';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import {
    LifecycleActions,
    LifecycleEditorShell,
    LifecycleNotice,
    LifecycleEditorLayout,
    LIFECYCLE_FIELD_HELP,
} from '@/shared/patterns/Lifecycle';
import { AmountInput, ChoiceChips, EditorField, FieldHelp } from '@/shared/patterns/EditorForm';
import {
    RetailerScopeColumn,
    ScopeChip,
    ScopeChipGrid,
    ScopeChipGroup,
    ScopeChipStatic,
    type RetailerScopeColumnProps,
} from '@/shared/patterns/ScopeChips';
import { ValidityCalendars } from '@/shared/patterns/ValidityCalendars';
import type {
    ReferralChannel,
    SalePromotionListItem,
    SalePromotionTransition,
} from '../api/promotionsContract';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import { listStatusOf } from '../lib/promotionsList';
import { formatValidity } from '../lib/promotionsRow';
import {
    referralPromotionFormSchema,
    type ReferralPromotionFormValues,
} from '../lib/referralPromotionForm';
import {
    CASH_METHOD_LABEL,
    CASH_METHODS,
    FINANCIER_LABEL,
    FINANCIERS,
    PAY_WITH_OPTIONS,
    TRIGGER_OPTIONS,
    triggerHasPurchase,
} from '../lib/referralPromotionOptions';
import type { ReferralFieldError } from '../lib/referralPromotionSaveError';
import { addDaysToDay } from '../lib/salePromotionDraft';
import {
    paymentOptionsSummary,
    PRODUCT_CATEGORY_OPTIONS,
    PRODUCT_TIER_OPTIONS,
} from '../lib/salePromotionFormOptions';
import { FIELD_INPUT, FIELD_LABEL, FieldMessage } from './editorParts';
import { promotionLifecycleActions } from './promotionLifecycle';
import { PromotionLogPanel } from './PromotionLogPanel';
import { PROMO_TONE, ReferralRewardFields } from './ReferralRewardFields';
import { MULTI_RETAILER_COPY, REFERRAL_PROMOTION_HELP } from '../constants/helpText';
import { SalePromotionBudgetEditor } from './SalePromotionBudgetEditor';
import {
    SalePromotionSegmentationEditor,
    SalePromotionSegmentationSummary,
} from './SalePromotionSegmentationEditor';
import { StoreScopeGroup, toggleIn } from './ScopePickers';

export type ReferralSaveOutcome = { ok: true } | { ok: false; field?: ReferralFieldError };

export interface ReferralPromotionEditorProps {
    /** `null` mientras se redacta una promo nueva. */
    promotion: SalePromotionListItem | null;
    channel: ReferralChannel;
    defaultValues: ReferralPromotionFormValues;
    sources: PromotionFormSources;
    now: Date;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    editable: boolean;
    canReadLog: boolean;
    saving: boolean;
    onSave: (values: ReferralPromotionFormValues) => Promise<ReferralSaveOutcome>;
    /** Descartar una promo nueva cierra el panel: no hay versión a la que volver. */
    onCloseNew: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onAction: (transition: SalePromotionTransition) => void;
    /** La columna «Cadena» del Alcance: varias al crear para quien abarca el silo, fija si no. */
    retailer?: RetailerScopeColumnProps;
    /** Ya hay copias creadas: la forma se congela para que el reintento mande lo mismo. */
    locked?: boolean;
}

const dirty = { shouldDirty: true } as const;
const NOTE = 'text-[10.5px] text-muted-foreground';
const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

function firstErrorMessage(errors: FieldErrors): string | undefined {
    for (const value of Object.values(errors)) {
        if (!value) continue;
        if (typeof value.message === 'string' && value.message) return value.message;
        const nested = firstErrorMessage(value as FieldErrors);
        if (nested) return nested;
    }
    return undefined;
}

export function ReferralPromotionEditor({
    promotion,
    channel,
    defaultValues,
    sources,
    now,
    today,
    editable,
    canReadLog,
    saving,
    onSave,
    onCloseNew,
    onDirtyChange,
    onAction,
    retailer,
    locked = false,
}: ReferralPromotionEditorProps) {
    const methods = useForm<ReferralPromotionFormValues>({
        defaultValues,
        resolver: zodResolver(referralPromotionFormSchema),
    });
    const {
        control,
        reset,
        setError,
        handleSubmit,
        formState: { isDirty },
    } = methods;
    const isNew = promotion === null;
    const showSave = editable && (isNew || isDirty);

    useEffect(() => {
        onDirtyChange(isNew || isDirty);
    }, [isDirty, isNew, onDirtyChange]);

    const submit = handleSubmit(
        async (values) => {
            const outcome = await onSave(values);
            if (outcome.ok) reset(values);
            else if (outcome.field)
                setError(outcome.field.path as FieldPath<ReferralPromotionFormValues>, {
                    type: 'server',
                    message: outcome.field.message,
                });
        },
        (formErrors) => {
            toast.error('Faltan datos para guardar', {
                description: firstErrorMessage(formErrors),
            });
        },
    );

    const discard = () => {
        if (isNew) onCloseNew();
        else reset(defaultValues);
    };

    const name = useWatch({ control, name: 'name' });

    return (
        <FormProvider {...methods}>
            <LifecycleEditorShell
                ariaLabel={
                    isNew ? 'Promo de referido nueva' : `Promo de referido ${promotion.name}`
                }
                title={name.trim() || (isNew ? 'Promo de referido nueva' : promotion.name)}
                dirty={promotion !== null && isDirty}
                subtitle={
                    isNew
                        ? 'Borrador nuevo · publicarla es un paso aparte'
                        : `Versión ${String(promotion.version)} · ${formatValidity(promotion)}`
                }
                status={promotion ? listStatusOf(promotion, now) : null}
                id={promotion ? { label: 'ID', value: promotion.promotionId } : undefined}
                tags={
                    <span className="inline-flex items-center rounded-[3px] bg-promo-soft px-[7px] py-[1.5px] text-[9.5px] font-bold uppercase tracking-[0.04em] text-promo">
                        Referido
                    </span>
                }
                actions={
                    promotion ? (
                        <LifecycleActions
                            actions={promotionLifecycleActions(promotion, now, onAction)}
                            dirty={isDirty}
                            menuLabel="Acciones de la promo"
                        />
                    ) : null
                }
                notices={renderNotice()}
                history={
                    promotion && canReadLog ? (
                        <PromotionLogPanel
                            promotionId={promotion.promotionId}
                            retailerId={promotion.retailerId}
                            compact
                        />
                    ) : null
                }
                saveBar={{
                    count: showSave ? 1 : 0,
                    message: isNew ? 'Promo de referido nueva sin guardar' : 'Cambios sin guardar',
                    saving,
                    onDiscard: discard,
                    onSave: () => {
                        void submit();
                    },
                }}
            >
                <fieldset disabled={!editable || locked} className="min-w-0">
                    <LifecycleEditorLayout
                        description={<DescriptionFields />}
                        levers={<RewardsBlock channel={channel} />}
                        audience={
                            <>
                                <p className={cn(NOTE, 'mb-2')}>
                                    Gana el{' '}
                                    <b className="text-foreground">cliente que recomienda</b>: el
                                    que ya compró y trae a alguien nuevo.
                                </p>
                                <SalePromotionSegmentationEditor
                                    segments={sources.segments}
                                    segmentsState={sources.segmentsState}
                                />
                            </>
                        }
                        scope={
                            <ReferralScope
                                sources={sources}
                                channel={channel}
                                retailer={retailer}
                            />
                        }
                        summaries={{
                            audience: (
                                <SalePromotionSegmentationSummary segments={sources.segments} />
                            ),
                            scope: <ScopeSummary channel={channel} />,
                        }}
                        whenPaid={<TriggerFields channel={channel} />}
                        rules={<ReferralRules channel={channel} />}
                        budget={
                            <SalePromotionBudgetEditor
                                consumedCents={promotion ? promotion.budgetConsumedCents : null}
                            />
                        }
                        validity={<ValidityField today={today} />}
                    />
                </fieldset>
            </LifecycleEditorShell>
        </FormProvider>
    );

    function renderNotice() {
        if (locked) {
            return (
                <LifecycleNotice tone="info" role="note">
                    {MULTI_RETAILER_COPY.locked}
                </LifecycleNotice>
            );
        }
        if (promotion && !editable) {
            return (
                <LifecycleNotice>
                    Esta promo ya no se edita: está vencida o archivada, o no tienes permiso de
                    editar.
                </LifecycleNotice>
            );
        }
        if (promotion && isDirty) {
            return (
                <LifecycleNotice tone="info" role="note">
                    Guardar crea la versión {promotion.version + 1}. Para cambiar el estado, guarda
                    o descarta primero.
                </LifecycleNotice>
            );
        }
        return null;
    }
}

function DescriptionFields() {
    const {
        register,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    return (
        <div className="flex min-w-0 flex-col gap-2.5">
            <EditorField
                label="Nombre"
                help={LIFECYCLE_FIELD_HELP.name}
                error={errors.name?.message}
            >
                {(id) => (
                    <input
                        id={id}
                        type="text"
                        placeholder="ej. Trae a un amigo · $200 a tu cuenta PCF"
                        aria-invalid={errors.name ? true : undefined}
                        className={FIELD_INPUT}
                        {...register('name')}
                    />
                )}
            </EditorField>
            <EditorField label="Descripción" help={LIFECYCLE_FIELD_HELP.description}>
                {(id) => (
                    <textarea
                        id={id}
                        rows={4}
                        placeholder="Cómo se la explica el vendedor al cliente"
                        className={FIELD_INPUT}
                        {...register('customerText')}
                    />
                )}
            </EditorField>
        </div>
    );
}

/** Veces que se paga un premio: el mismo monto con unidad de los demás campos. */
function TimesField({
    label,
    help,
    name,
}: {
    label: string;
    help: string;
    name: 'maxRewardsPerReferral' | 'maxReferredRewards';
}) {
    const {
        register,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    const error = errors[name]?.message;
    return (
        <EditorField label={label} help={help} error={error}>
            {(id) => (
                <AmountInput
                    id={id}
                    inputMode="numeric"
                    placeholder="1"
                    unit="veces"
                    aria-invalid={error ? true : undefined}
                    {...register(name)}
                />
            )}
        </EditorField>
    );
}

function RewardsBlock({ channel }: { channel: ReferralChannel }) {
    const { control, setValue } = useFormContext<ReferralPromotionFormValues>();
    const referredEnabled = useWatch({ control, name: 'referredEnabled' });
    return (
        <div className="flex min-w-0 flex-col gap-2.5">
            <EditorField
                label="Premio de quien recomienda"
                help={REFERRAL_PROMOTION_HELP.referrerReward}
                note="Elige un solo premio para quien recomienda. Se abona cuando ocurre el evento del referido, no al publicar."
            >
                <ReferralRewardFields holder="referrerReward" channel={channel} />
            </EditorField>
            <TimesField
                label="Veces que se le paga a quien refiere por cada referido"
                help={REFERRAL_PROMOTION_HELP.maxRewardsPerReferral}
                name="maxRewardsPerReferral"
            />
            <EditorField
                label="El referido también gana"
                help={REFERRAL_PROMOTION_HELP.referredEnabled}
                className="border-t border-border/50 pt-2.5"
            >
                <ChoiceChips
                    options={[
                        { value: 'OFF', label: 'No' },
                        { value: 'ON', label: 'Sí' },
                    ]}
                    value={referredEnabled ? 'ON' : 'OFF'}
                    onPick={(value) => {
                        setValue('referredEnabled', value === 'ON', dirty);
                    }}
                />
            </EditorField>
            {referredEnabled ? (
                <div className="flex flex-col gap-2.5 rounded-md border border-promo-soft-border bg-promo-soft/40 p-2.5">
                    <EditorField
                        label="Premio del recomendado"
                        help={REFERRAL_PROMOTION_HELP.referredReward}
                    >
                        <ReferralRewardFields holder="referredReward" channel={channel} />
                    </EditorField>
                    <TimesField
                        label="Veces que se le paga al referido"
                        help={REFERRAL_PROMOTION_HELP.maxReferredRewards}
                        name="maxReferredRewards"
                    />
                </div>
            ) : null}
        </div>
    );
}

/** Tiendas y, si el evento es una compra de venta, el tipo y la gama de esa compra. */
function ReferralScope({
    sources,
    channel,
    retailer,
}: {
    sources: PromotionFormSources;
    channel: ReferralChannel;
    retailer?: RetailerScopeColumnProps;
}) {
    const { control, setValue } = useFormContext<ReferralPromotionFormValues>();
    const [storeIds, trigger, categories, tiers] = useWatch({
        control,
        name: ['storeIds', 'trigger', 'productCategories', 'tiers'],
    });
    const showProducts = channel === 'SALE' && triggerHasPurchase(trigger);
    const multiChain = retailer?.mode === 'choose' && retailer.selected.length > 1;
    return (
        <ScopeChipGrid>
            {retailer ? (
                <RetailerScopeColumn
                    {...retailer}
                    {...(retailer.mode === 'choose'
                        ? {
                              // Las tiendas son de una cadena: al cambiar de cadena se sueltan.
                              onToggle: (retailerId: string) => {
                                  if (storeIds.length > 0) setValue('storeIds', [], dirty);
                                  retailer.onToggle(retailerId);
                              },
                          }
                        : {})}
                />
            ) : null}
            {showProducts ? (
                <>
                    <ScopeChipGroup
                        title="Tipo de la compra"
                        help={REFERRAL_PROMOTION_HELP.purchaseType}
                    >
                        {PRODUCT_CATEGORY_OPTIONS.map((option) => (
                            <ScopeChip
                                key={option.value}
                                dim="tier"
                                label={option.label}
                                selected={categories.includes(option.value)}
                                onToggle={() => {
                                    setValue(
                                        'productCategories',
                                        toggleIn(categories, option.value),
                                        dirty,
                                    );
                                }}
                            />
                        ))}
                    </ScopeChipGroup>
                    <ScopeChipGroup
                        title="Gama de la compra"
                        help={REFERRAL_PROMOTION_HELP.purchaseTier}
                    >
                        {PRODUCT_TIER_OPTIONS.map((option) => (
                            <ScopeChip
                                key={option.value}
                                dim="tier"
                                tone={PROMO_TONE}
                                label={option.label}
                                selected={tiers.includes(option.value)}
                                onToggle={() => {
                                    setValue('tiers', toggleIn(tiers, option.value), dirty);
                                }}
                            />
                        ))}
                    </ScopeChipGroup>
                </>
            ) : null}
            {multiChain ? (
                <ScopeChipGroup
                    title="Tienda"
                    help={REFERRAL_PROMOTION_HELP.stores}
                    footer={<p className={cn(NOTE, 'mt-1.5')}>{MULTI_RETAILER_COPY.stores}</p>}
                >
                    <ScopeChipStatic dim="store" label="Todas" />
                </ScopeChipGroup>
            ) : (
                <StoreScopeGroup
                    withAllChip
                    help={REFERRAL_PROMOTION_HELP.stores}
                    searchLabel="Buscar tienda de la promo"
                    stores={sources.stores}
                    storesState={sources.storesState}
                    selected={storeIds}
                    onChange={(next) => {
                        setValue('storeIds', next, dirty);
                    }}
                />
            )}
        </ScopeChipGrid>
    );
}

/** Lo elegido en Alcance en una línea, para la derecha del título. */
function ScopeSummary({ channel }: { channel: ReferralChannel }) {
    const { control } = useFormContext<ReferralPromotionFormValues>();
    const [storeIds, trigger, categories, tiers] = useWatch({
        control,
        name: ['storeIds', 'trigger', 'productCategories', 'tiers'],
    });
    const stores =
        storeIds.length === 0
            ? 'Todas las tiendas'
            : storeIds.length === 1
              ? '1 tienda'
              : `${String(storeIds.length)} tiendas`;
    const products =
        channel === 'SALE' && triggerHasPurchase(trigger)
            ? categories.length + tiers.length === 0
                ? 'cualquier compra'
                : 'compra acotada'
            : null;
    return (
        <b className="font-semibold uppercase tracking-[0.04em] text-foreground">
            {products ? `${stores} · ${products}` : stores}
        </b>
    );
}

function TriggerFields({ channel }: { channel: ReferralChannel }) {
    const {
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    const trigger = useWatch({ control, name: 'trigger' });
    const repeatField =
        trigger === 'NTH_PURCHASE'
            ? ({ name: 'purchaseNumber', label: 'Compra número' } as const)
            : trigger === 'INSTALLMENT_STREAK'
              ? ({ name: 'installmentStreak', label: 'Cupones seguidos' } as const)
              : null;
    const repeatError = repeatField ? errors[repeatField.name]?.message : undefined;

    return (
        <div className="flex flex-col gap-2.5">
            <EditorField
                label="Evento del referido"
                help={REFERRAL_PROMOTION_HELP.trigger}
                error={errors.trigger?.message}
            >
                <div
                    role="group"
                    aria-label="Evento"
                    className="grid grid-cols-1 gap-1.5 sm:grid-cols-2"
                >
                    {TRIGGER_OPTIONS[channel].map((option) => {
                        const on = trigger === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    setValue('trigger', option.value, dirty);
                                }}
                                className={cn(
                                    'flex items-start gap-2 rounded-md border px-2.5 py-2 text-left transition-colors disabled:opacity-60',
                                    on
                                        ? 'border-promo-soft-border bg-promo-soft'
                                        : 'border-border bg-card hover:bg-accent/40',
                                )}
                            >
                                <span
                                    aria-hidden
                                    className={cn(
                                        'mt-0.5 size-2.5 flex-none rounded-full border-[1.5px]',
                                        on ? 'border-promo bg-promo' : 'border-border',
                                    )}
                                />
                                <span className="min-w-0">
                                    <span className="block text-[11.5px] font-semibold text-foreground">
                                        {option.label}
                                    </span>
                                    <span className="block text-[10px] text-muted-foreground">
                                        {option.hint}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </EditorField>
            {repeatField ? (
                <EditorField
                    label={repeatField.label}
                    help={REFERRAL_PROMOTION_HELP.repeat}
                    error={repeatError}
                >
                    {(id) => (
                        <AmountInput
                            id={id}
                            inputMode="numeric"
                            unit="mínimo 2"
                            aria-invalid={repeatError ? true : undefined}
                            {...register(repeatField.name)}
                        />
                    )}
                </EditorField>
            ) : null}
        </div>
    );
}

function ReferralRules({ channel }: { channel: ReferralChannel }) {
    const {
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    const [trigger, payWith, cashMethods, financiers, customOptions] = useWatch({
        control,
        name: ['trigger', 'payWith', 'cashMethods', 'financiers', 'customPaymentOptions'],
    });
    const purchase = triggerHasPurchase(trigger);
    const showPay = channel === 'SALE' && purchase;
    const payOptions =
        customOptions.length > 0
            ? [...PAY_WITH_OPTIONS, { value: 'CUSTOM' as const, label: 'Personalizado' }]
            : PAY_WITH_OPTIONS;

    return (
        <div className="flex flex-col gap-3">
            {showPay ? (
                <EditorField label="Cómo paga" help={REFERRAL_PROMOTION_HELP.payWith}>
                    <div className="flex flex-col gap-2">
                        <ChoiceChips
                            options={payOptions}
                            value={payWith}
                            onPick={(value) => {
                                setValue('payWith', value, dirty);
                            }}
                        />
                        {payWith === 'CUSTOM' ? (
                            <p role="note" className={cn(NOTE, 'pl-3')}>
                                {`Personalizado: ${lowerFirst(paymentOptionsSummary(customOptions))}. Se guarda tal cual; elige otra opción para cambiarlo.`}
                            </p>
                        ) : null}
                        {payWith === 'CASH' ? (
                            <MultiChips
                                label="Medio de cobro"
                                help={REFERRAL_PROMOTION_HELP.cashMethods}
                                empty="Sin elegir: cuenta cualquier medio de contado."
                                options={CASH_METHODS.map((value) => ({
                                    value,
                                    label: CASH_METHOD_LABEL[value],
                                }))}
                                selected={cashMethods}
                                onChange={(next) => {
                                    setValue('cashMethods', next, dirty);
                                }}
                            />
                        ) : null}
                        {payWith === 'OTHER' ? (
                            <MultiChips
                                label="Financiera"
                                help={REFERRAL_PROMOTION_HELP.financiers}
                                empty="Sin elegir: cuenta cualquier otra financiera."
                                options={FINANCIERS.map((value) => ({
                                    value,
                                    label: FINANCIER_LABEL[value],
                                }))}
                                selected={financiers}
                                onChange={(next) => {
                                    setValue('financiers', next, dirty);
                                }}
                            />
                        ) : null}
                    </div>
                </EditorField>
            ) : null}
            <div className="flex flex-wrap items-start gap-4">
                {purchase ? (
                    <ConditionField
                        label={
                            channel === 'SALE'
                                ? 'Monto mínimo de la compra'
                                : 'Monto mínimo del crédito'
                        }
                        help={REFERRAL_PROMOTION_HELP.minAmount}
                        unit="$ MXN"
                        placeholder="Cualquiera"
                        error={errors.minAmount?.message}
                        registration={register('minAmount')}
                    />
                ) : null}
                <ConditionField
                    label="Ventana desde que fue referido"
                    help={REFERRAL_PROMOTION_HELP.windowDays}
                    unit="días"
                    placeholder="Sin límite"
                    error={errors.windowDays?.message}
                    registration={register('windowDays')}
                />
                <ConditionField
                    label="Máximo de premios por cliente que recomienda"
                    help={REFERRAL_PROMOTION_HELP.maxRewardsPerReferrer}
                    unit="premios"
                    placeholder="Sin máximo"
                    error={errors.maxRewardsPerReferrer?.message}
                    registration={register('maxRewardsPerReferrer')}
                />
            </div>
            {purchase ? null : (
                <p className={NOTE}>
                    Al abrir su cuenta no hay compra: no se filtra por forma de pago, monto ni gama.
                </p>
            )}
        </div>
    );
}

function MultiChips<T extends string>({
    label,
    help,
    empty,
    options,
    selected,
    onChange,
}: {
    label: string;
    help: string;
    empty: string;
    options: readonly { value: T; label: string }[];
    selected: T[];
    onChange: (next: T[]) => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2 pl-3">
            <span className="flex items-center gap-1">
                <span className={FIELD_LABEL}>{label}</span>
                <FieldHelp label={label} help={help} />
            </span>
            <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
                {options.map((option) => (
                    <ScopeChip
                        key={option.value}
                        dim="tier"
                        tone={PROMO_TONE}
                        label={option.label}
                        selected={selected.includes(option.value)}
                        onToggle={() => {
                            onChange(toggleIn(selected, option.value));
                        }}
                    />
                ))}
            </div>
            {selected.length === 0 ? <span className={NOTE}>{empty}</span> : null}
        </div>
    );
}

function ConditionField({
    label,
    help,
    unit,
    placeholder,
    error,
    registration,
}: {
    label: string;
    help: string;
    unit: string;
    placeholder: string;
    error: string | undefined;
    registration: ReturnType<
        ReturnType<typeof useFormContext<ReferralPromotionFormValues>>['register']
    >;
}) {
    return (
        <EditorField label={label} help={help} error={error}>
            {(id) => (
                <AmountInput
                    id={id}
                    inputMode="numeric"
                    placeholder={placeholder}
                    unit={unit}
                    aria-invalid={error ? true : undefined}
                    {...registration}
                />
            )}
        </EditorField>
    );
}

/** Vigencia con los calendarios del portal: ∞ Permanente, Desde y Hasta. */
function ValidityField({ today }: { today: string }) {
    const {
        control,
        setValue,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    const [validFrom, permanent, validUntil] = useWatch({
        control,
        name: ['validFrom', 'permanent', 'validUntil'],
    });
    const quiet = { shouldDirty: true, shouldValidate: false } as const;
    return (
        <>
            <ValidityCalendars
                validFrom={validFrom === '' ? null : validFrom}
                validTo={permanent || validUntil === '' ? null : validUntil}
                today={today}
                onChange={({ validFrom: from, validTo }) => {
                    setValue('validFrom', from ?? '', quiet);
                    setValue('validUntil', validTo ?? '', quiet);
                    if (validTo) setValue('permanent', false, quiet);
                }}
                onTogglePermanent={(isPermanent) => {
                    if (isPermanent) {
                        setValue('permanent', false, quiet);
                        setValue('validUntil', addDaysToDay(validFrom || today, 30), quiet);
                    } else {
                        setValue('permanent', true, quiet);
                        setValue('validUntil', '', quiet);
                    }
                }}
            />
            <FieldMessage message={errors.validUntil?.message} />
        </>
    );
}
